import { Component, EventEmitter, Inject, Input, OnInit, Output } from '@angular/core';
import { AbstractControl, FormControl, FormGroup, UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { ContentService } from '@blk/cwp-ng-framework';
import { AlertType, DzToastAlert } from '@blk/daisy/combinations/notifications';
import { TableHeader } from '@blk/daisy/modules/table';
import { V1ExternalAccountInfo } from '@blk/portfolio-security-server/accountInfo';
import { APP_STORE_CONFIG_TOKEN } from '@ddt-web/config';
import { InjectConfig, PaConfigKey } from '@ddt-web/config-layer';
import { AppStoreConfig } from '@ddt-web/data-service';
import { PartnerPlatformControlsList } from '@ddt-web/domain';
import { AccountErrors, ApplicationStateStoreProxyService, PartnerAccount, PartnerPlatform } from '@ddt-web/shared-stores';
import { StoreService } from '@ddt-web/store-service';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import * as cloneDeep from 'lodash/cloneDeep';
import * as isEqual from 'lodash/isEqual';
import { distinctUntilChanged, withLatestFrom } from 'rxjs/operators';
import { v4 as uuid } from 'uuid';

import { DcrPrefixMapping, PartnerPlatformConstants } from '../../constants';
import { PartnerPortfolioValidatorService } from '../../services';

@UntilDestroy()
@Component({
	selector: 'ipp-portfolio-edit',
	templateUrl: './portfolio-edit.component.html',
	styleUrls: ['./portfolio-edit.component.scss']
})
export class PortfolioEditComponent implements OnInit {
	@InjectConfig(PaConfigKey.PARTNER_PLATFORM_CONTROLS_LIST) public readonly partnerPlatformControlsList: PartnerPlatformControlsList;

	@Input() public missingPortfolioDataIds: string[] = [];
	@Output() public portfolioEditNotifications: EventEmitter<DzToastAlert> = new EventEmitter();
	@Output() public isFormValid: EventEmitter<'VALID' | 'INVALID'> = new EventEmitter();

	public tableHeaders: Array<any> = [];
	public portfolioForm;
	public showWarningBlockAlert = false;
	public marketDataIssuesAccountIds = [];

	private selectedPortfolios: Map<string, V1ExternalAccountInfo>;
	private selectedPartner: PartnerPlatform;
	private accountErrorMap: Map<string, AccountErrors>;
	private accounts: PartnerAccount[];

	constructor(
		private formBuilder: UntypedFormBuilder,
		private storeService: StoreService,
		private contentService: ContentService,
		private applicationStateStoreProxyService: ApplicationStateStoreProxyService,
		private partnerPortfolioValidatorService: PartnerPortfolioValidatorService,
		@Inject(APP_STORE_CONFIG_TOKEN) public storeConfig: AppStoreConfig
	) { }

	public ngOnInit(): void {
		this.initSelectedPartnerAndPortfolios();
	}

	public handleRevertDisplayName(portfolioIndex: number, accountId: string): void {
		const portfolio = this.getPortfolios().controls[portfolioIndex] as UntypedFormGroup;
		this.getPortfolioDisplayName(portfolio).setValue(this.getPortfolioName(portfolio).value);
		this.getEnableRevert(portfolio).setValue(false);

		delete this.selectedPortfolios.get(accountId).additionalAttributes[PartnerPlatformConstants.MODIFIED_DISPLAY_NAME_FIELD_NAME];

		this.applicationStateStoreProxyService.updateImportPartnerPortfoliosSelectedPortfolios(this.selectedPortfolios);
	}

	public onPortfolioDisplayNameChange(updatedPortfolioName: string, portfolioIndex: number, accountId: string): void {
		const portfolio = this.getPortfolios().controls[portfolioIndex] as UntypedFormGroup;
		const portfolioName = this.getPortfolioName(portfolio);
		const portfolioDisplayName = this.getPortfolioDisplayName(portfolio);
		portfolioDisplayName.setValue(updatedPortfolioName);

		this.setAdditionalAttributeOnPortfolio(this.selectedPortfolios.get(accountId), PartnerPlatformConstants.MODIFIED_DISPLAY_NAME_FIELD_NAME, updatedPortfolioName);
		this.setAdditionalAttributeOnPortfolio(this.selectedPortfolios.get(accountId), PartnerPlatformConstants.MODIFIED_DISPLAY_NAME_HAS_ERRORS, 'false');

		this.applicationStateStoreProxyService.updateImportPartnerPortfoliosSelectedPortfolios(this.selectedPortfolios);
		this.getEnableRevert(portfolio).setValue(portfolioName.value !== portfolioDisplayName.value);

		this.portfolioForm.get('portfolios').controls.forEach(control => control.get('portfolioDisplayName').updateValueAndValidity());
	}

	public deletePortfolio(portfolioIndex: number, accountId: string): void {
		this.getPortfolios().removeAt(portfolioIndex);
		this.selectedPortfolios.delete(accountId);
		this.accountErrorMap.delete(accountId);
		this.accounts = this.accounts.filter(acc => acc.id !== accountId);
		this.applicationStateStoreProxyService.updateImportPartnerPortfoliosState({
			selectedPortfolios: this.selectedPortfolios,
			accountErrorMap: this.accountErrorMap,
			portfolioData: this.accounts
		});
	}

	private getPortfolios(): UntypedFormArray {
		return this.portfolioForm?.get('portfolios') as UntypedFormArray;
	}

	private getPortfolioDisplayName(formResponse: UntypedFormGroup): UntypedFormControl {
		return formResponse.get('portfolioDisplayName') as UntypedFormControl;
	}

	private getPortfolioName(formResponse: UntypedFormGroup): UntypedFormControl {
		return formResponse.get('portfolioName') as UntypedFormControl;
	}

	private getEnableRevert(formResponse: UntypedFormGroup): UntypedFormControl {
		return formResponse.get('enableRevert') as UntypedFormControl;
	}

	private buildForm(selectedPortfolios: V1ExternalAccountInfo[], accountErrors: Map<string, AccountErrors>): void {
		const portfoliosArray = selectedPortfolios.map(portfolio => {
			const errorInfo = accountErrors.get(portfolio.id);
			const hasMarketValueErrors = errorInfo.hasMarketValueErrors;
			const invalidPortfolioKey = this.partnerPortfolioValidatorService.buildInvalidPortfolioErrorKey(errorInfo);

			return this.formBuilder.group({
				portfolioName: portfolio.name,
				portfolioDisplayName: new FormControl(
					portfolio.additionalAttributes?.[PartnerPlatformConstants.MODIFIED_DISPLAY_NAME_FIELD_NAME]?.stringValue ?? portfolio.name,
					[
						this.duplicateNameValidator(portfolio),
						Validators.required,
						this.duplicateNameInImportValidator()
					]
				),
				accountId: new FormControl(portfolio.id),
				platform: new FormControl(this.partnerPlatformControlsList?.[this.selectedPartner.id]?.displayName),
				enableRevert: new FormControl(!!portfolio.additionalAttributes?.modifiedDisplayName),
				hasMarketValueErrors: new FormControl(hasMarketValueErrors),
				invalidPortfolioInfo: this.formBuilder.group({
					hasError: new FormControl(!!invalidPortfolioKey),
					dcrKey: new FormControl(DcrPrefixMapping[invalidPortfolioKey]?.dcrKey),
					dcrValue: new FormControl(DcrPrefixMapping[invalidPortfolioKey]?.dcrValue)
				})
			});
		});

		this.portfolioForm = this.formBuilder.group({ portfolios: this.formBuilder.array(portfoliosArray) });

		this.isFormValid.emit(this.portfolioForm.status);
		this.portfolioForm.statusChanges.pipe(
			distinctUntilChanged(),
			untilDestroyed(this)
		).subscribe((status) => {
			this.isFormValid.emit(status);
		});
	}

	private initSelectedPartnerAndPortfolios(): void {
		this.applicationStateStoreProxyService.getImportPartnerPortfoliosState()
			.pipe(
				untilDestroyed(this),
				withLatestFrom(this.storeService.selectAll(this.storeConfig['userPortfolios']))
			)
			.subscribe(([state, userPortfolioState]) => {
				this.selectedPartner = state.partnerPlatform;
				this.accountErrorMap = state.accountErrorMap;
				this.accounts = state.portfolioData;
				this.marketDataIssuesAccountIds =
					Array.from(this.accountErrorMap, ([key, value]) => ({ key, value }))
						.filter(entry => entry.value.hasMarketValueErrors)
						.map(entry => entry.key);
				this.showWarningBlockAlert = this.marketDataIssuesAccountIds.length > 0;
				if (!isEqual(this.selectedPortfolios, state.selectedPortfolios)) {
					this.selectedPortfolios = state.selectedPortfolios;
					this.handlePreviouslyImportedPortfolios(Object.values(userPortfolioState.entities), state.accountErrorMap);
				}
				this.tableHeaders = [
					new TableHeader(
						this.contentService.get([`${this.selectedPartner.id}_PORTFOLIO_LIST_DISPLAY_NAME`, 'PARTNER_PORTFOLIO_LIST_DISPLAY_NAME'], 'Display Name'), null, null, null, null, null, 'displayName'),
					new TableHeader(
						this.contentService.get([`${this.selectedPartner.id}_PORTFOLIO_LIST_ACCOUNT_ID`, 'PARTNER_PORTFOLIO_LIST_ACCOUNT_ID'], 'Account ID'), null, null, null, null, null, 'accountId'
					),
					new TableHeader(
						this.contentService.get([`${this.selectedPartner.id}_PORTFOLIO_LIST_PLATFORM`, 'PARTNER_PORTFOLIO_LIST_PLATFORM'], 'Platform'), null, null, null, null, null, 'platform'
					),
					new TableHeader(
						this.contentService.get([`${this.selectedPartner.id}_PORTFOLIO_LIST_ACTIONS`, 'PARTNER_PORTFOLIO_LIST_ACTIONS'], 'Actions'), null, null, null, 'actions-header', null, 'actions'
					)
				];
			});
	}

	private handlePreviouslyImportedPortfolios(userPortfolios, accountErrorMap: Map<string, AccountErrors>): void {
		const selectedPortfoliosClone: Map<string, V1ExternalAccountInfo> = cloneDeep(this.selectedPortfolios);
		let hasReimportedPortfolios = false;
		Array.from(selectedPortfoliosClone.entries()).forEach(([accountId, portfolio]) => {
			const userPortfolio = userPortfolios.find(port =>
				port.originPlatform?.platform === this.selectedPartner.id && port.encrypted?.originPartnerAccountId === portfolio.id);
			if (userPortfolio) {
				hasReimportedPortfolios = true;
				selectedPortfoliosClone.set(accountId, { ...portfolio, name: userPortfolio.description });
			}
		});

		if (hasReimportedPortfolios) {
			this.emitReimportWarning();
			this.applicationStateStoreProxyService.updateImportPartnerPortfoliosSelectedPortfolios(selectedPortfoliosClone);
		}
		this.buildForm(Array.from(selectedPortfoliosClone.values()), accountErrorMap);
	}

	private emitReimportWarning(): void {
		const dcrKey = DcrPrefixMapping.REIMPORT?.dcrKey;
		this.portfolioEditNotifications.emit({
			id: uuid(),
			type: AlertType.warning,
			message: this.contentService.get(dcrKey, DcrPrefixMapping.REIMPORT.dcrValue)
		});
	}

	private setAdditionalAttributeOnPortfolio(selectedPortfolio: V1ExternalAccountInfo, attributeKey: string, stringValue: string): void {
		if (!selectedPortfolio.additionalAttributes) {
			selectedPortfolio.additionalAttributes = {};
		}

		selectedPortfolio.additionalAttributes[attributeKey] = { stringValue };
	}

	private duplicateNameValidator(portfolio: V1ExternalAccountInfo): ValidatorFn {
		return (control: AbstractControl): ValidationErrors | null => {
			const portfolioNameHasErrors = portfolio.additionalAttributes[PartnerPlatformConstants.MODIFIED_DISPLAY_NAME_HAS_ERRORS]?.stringValue === 'true';
			const portfolioHasModifiedName = portfolio.additionalAttributes[PartnerPlatformConstants.MODIFIED_DISPLAY_NAME_FIELD_NAME]?.stringValue === control.value;

			if (portfolioHasModifiedName && portfolioNameHasErrors) {
				return { modifiedDisplayNameHasErrors: true };
			}

			return null;
		};
	}

	private duplicateNameInImportValidator(): ValidatorFn {
		return (control: AbstractControl) => {
			const currentName = control.value.trim();
			const currentNameCount = (this.getPortfolios()?.controls || [])
				.map((formGroup: FormGroup) => this.getPortfolioDisplayName(formGroup)?.value?.trim())
				.filter((displayName: string) => displayName === currentName).length;

			if (currentNameCount > 1) {
				return {
					duplicateNameInImport: true
				};
			}

			return null;
		};
	}
}
